/**
 * Maps Better Auth error responses to user-facing messages. Codes without an
 * override keep the server's message, which may carry detail such as a ban reason.
 */
import type {IAuthFlowError} from '@shared/auth_types';
import {authClient} from '@shared_ui/auth_client';

/** The error object shape better-auth client calls resolve with. */
export interface IAuthClientError {
   code?: string | null;
   message?: string | null;
}

/** IAuthFlowError with the code narrowed to the client's known codes. */
export interface IAuthTypedFlowError extends IAuthFlowError {
   code: AuthErrorCode | null;
}

/** Every error code the configured client can return, from its typed $ERROR_CODES. */
export type AuthErrorCode = keyof typeof authClient.$ERROR_CODES & string;

/** Narrow a raw code string to AuthErrorCode; null when absent or unknown. */
export function asAuthErrorCode(code: string | null | undefined): AuthErrorCode | null {
   if (code == null) {
      return null;
   }
   return code in authClient.$ERROR_CODES ? (code as AuthErrorCode) : null;
}

/** True when the error carries the given code. */
export function isAuthError(error: IAuthClientError | null, code: AuthErrorCode): boolean {
   return error != null && error.code === code;
}

/** Friendly overrides for codes whose server wording is too terse/technical. */
const FRIENDLY_MESSAGES: Partial<Record<AuthErrorCode, string>> = {
   INVALID_EMAIL_OR_PASSWORD: 'Incorrect email or password',
   INVALID_USERNAME_OR_PASSWORD: 'Incorrect username or password',
   USER_ALREADY_EXISTS: 'An account with that email already exists',
   USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'An account with that email already exists',
   USERNAME_IS_ALREADY_TAKEN: 'That username is taken',
   INVALID_USERNAME: 'Usernames can use letters, numbers, dots, and underscores',
   USERNAME_TOO_SHORT: 'That username is too short',
   USERNAME_TOO_LONG: 'That username is too long',
   PASSWORD_TOO_SHORT: 'That password is too short',
   PASSWORD_TOO_LONG: 'That password is too long',
   INVALID_PASSWORD: 'Current password is incorrect',
   INVALID_EMAIL: 'Enter a valid email address',
   INVALID_TOKEN: 'This link is invalid, has expired, or was already used',
   TOKEN_EXPIRED: 'This link has expired',
   EMAIL_NOT_VERIFIED: 'Verify your email address before signing in',
   EMAIL_VERIFICATION_REQUIRED_FOR_INVITATION:
      'Verify your email address to view your invitations',
};

/**
 * Resolve an auth-flow error to the message shown to the user: friendly
 * override by code, else the server message, else the caller's fallback.
 */
export function authErrorMessage(error: IAuthClientError | null, fallback: string): string {
   if (error == null) {
      return fallback;
   }

   const override = error.code == null ? null : FRIENDLY_MESSAGES[error.code as AuthErrorCode];
   return override ?? error.message ?? fallback;
}
